import { useCallback } from 'react';
import { Navigate, Outlet, useMatch, useNavigate } from 'react-router-dom';

import { PageContainer, Tab } from '@pukka/ui-components';
import { DefaultActionBar } from '@pukka/ui-composite';
import { useFlagWithFallback } from '@pukka/unleash';
import { useSnapshot } from 'valtio';

import { BillingPersistedState, BillingState } from '../Billing.state';

interface RecurringPageProps {
  readonly PersistedState?: typeof BillingPersistedState;
  readonly State?: typeof BillingState;
}

const RECURRING_TAB_DATA = [
  {
    value: 'payment',
    title: 'Payment',
  },
  {
    value: 'outlet',
    title: 'Outlet',
  },
];

function RecurringPage({ PersistedState = BillingPersistedState, State = BillingState }: RecurringPageProps) {
  const match = useMatch('v2/settings/billing/recurring/:tabId');
  const navigate = useNavigate();
  const currentTabId = match?.params.tabId;
  const [enableRecurringPayment] = useFlagWithFallback('recurring-payment-backoffice');
  const { isRecurring } = useSnapshot(PersistedState);
  const { useBillingData } = State;

  const onChangeTab = useCallback(
    (tab: string) => {
      navigate(`./${tab}`, {
        relative: 'path',
      });
    },
    [navigate],
  );

  useBillingData();

  if (!enableRecurringPayment || !isRecurring) {
    return <Navigate to="/v2/settings/billing" />;
  }

  return enableRecurringPayment ? (
    <PageContainer className="w-full">
      <DefaultActionBar pageTitle="Billing" />
      <Tab data={RECURRING_TAB_DATA} defaultValue="payment" value={currentTabId ?? 'payment'} onChange={onChangeTab} />
      {!currentTabId ? <Navigate to="payment" /> : null}
      <Outlet />
    </PageContainer>
  ) : /* istanbul ignore next */ null;
}

export { RecurringPage };
